import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShoppingCart, FaArrowLeft, FaMinus, FaPlus, FaCheck, FaTruck, FaStore, FaShieldAlt, FaStar } from 'react-icons/fa';
import { partsStoreService } from '../services/partsStoreService';
import { useCart } from '../contexts/CartContext';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    loadProduct();
    window.scrollTo(0, 0);
  }, [slug]);

  const loadProduct = async () => {
    setLoading(true);
    const { data, error } = await partsStoreService.getProductBySlug(slug);
    if (error || !data) {
      navigate('/parts');
      return;
    }
    setProduct(data);
    setSelectedImage(0);
    setQuantity(1);

    const { data: related } = await partsStoreService.getRelatedProducts(data);
    setRelatedProducts(related);
    setLoading(false);
  };

  const handleAddToCart = () => {
    if (!product) return;
    addItem(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const formatPrice = (p) => `$${parseFloat(p).toFixed(2)}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400 text-lg">Loading product...</div>
      </div>
    );
  }

  if (!product) return null;

  const images = product.images?.length > 0 ? product.images : [null];
  const inStock = product.stock_quantity > 0;
  const specs = product.specifications || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 pt-6 pb-2">
        <nav className="flex items-center text-sm text-gray-500">
          <Link to="/" className="hover:text-blue-600">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/parts" className="hover:text-blue-600">Parts Store</Link>
          <span className="mx-2">/</span>
          <Link to={`/parts?category=${encodeURIComponent(product.category)}`} className="hover:text-blue-600">{product.category}</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900 font-medium truncate">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="aspect-square bg-gray-100 relative">
                {images[selectedImage] ? (
                  <img
                    src={images[selectedImage]}
                    alt={product.name}
                    className="w-full h-full object-contain p-4"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300 text-8xl">
                    🔧
                  </div>
                )}
                {product.sale_price && (
                  <span className="absolute top-4 right-4 bg-red-500 text-white text-sm px-3 py-1.5 rounded-full font-bold">
                    {Math.round((1 - product.sale_price / product.price) * 100)}% OFF
                  </span>
                )}
              </div>

              {images.length > 1 && (
                <div className="flex gap-2 p-4 overflow-x-auto">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${
                        selectedImage === i ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {img ? (
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-lg">🔧</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="space-y-6"
          >
            <div>
              <p className="text-sm text-blue-600 font-medium mb-2">{product.category}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-3">
                {product.name}
              </h1>
              {product.sku && (
                <p className="text-sm text-gray-400">SKU: {product.sku}</p>
              )}
            </div>

            {/* Price */}
            <div className="flex items-end gap-3">
              {product.sale_price ? (
                <>
                  <span className="text-3xl font-bold text-green-600">{formatPrice(product.sale_price)}</span>
                  <span className="text-xl text-gray-400 line-through">{formatPrice(product.price)}</span>
                  <span className="text-sm bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">
                    Save {formatPrice(product.price - product.sale_price)}
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold text-gray-900">{formatPrice(product.price)}</span>
              )}
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              {inStock ? (
                <>
                  <span className="w-2.5 h-2.5 bg-green-500 rounded-full" />
                  <span className="text-sm font-medium text-green-700">
                    In Stock ({product.stock_quantity} available)
                  </span>
                </>
              ) : (
                <>
                  <span className="w-2.5 h-2.5 bg-red-500 rounded-full" />
                  <span className="text-sm font-medium text-red-700">Out of Stock</span>
                </>
              )}
            </div>

            {/* Condition */}
            {product.condition && (
              <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium">
                <FaShieldAlt className="text-blue-500" />
                Condition: {product.condition.charAt(0).toUpperCase() + product.condition.slice(1)}
              </div>
            )}

            {/* Description */}
            {product.description && (
              <div className="prose prose-sm max-w-none text-gray-600">
                <p className="whitespace-pre-line">{product.description}</p>
              </div>
            )}

            {/* Add to Cart */}
            {inStock && (
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-3 hover:bg-gray-100 transition-colors"
                  >
                    <FaMinus className="text-xs text-gray-500" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={product.stock_quantity}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(Math.max(1, parseInt(e.target.value) || 1), product.stock_quantity))}
                    className="w-14 text-center border-0 font-medium focus:ring-0"
                  />
                  <button
                    onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                    className="px-3 py-3 hover:bg-gray-100 transition-colors"
                  >
                    <FaPlus className="text-xs text-gray-500" />
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  className={`flex-1 flex items-center justify-center gap-3 py-3.5 rounded-xl font-semibold text-white transition-all duration-300 ${
                    added
                      ? 'bg-green-500 scale-[1.02]'
                      : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'
                  }`}
                >
                  {added ? (
                    <><FaCheck /> Added to Cart</>
                  ) : (
                    <><FaShoppingCart /> Add to Cart — {formatPrice((product.sale_price || product.price) * quantity)}</>
                  )}
                </button>
              </div>
            )}

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <FaTruck className="text-blue-500 text-lg" />
                <div>
                  <p className="text-sm font-medium text-gray-900">$9.99 Flat Rate</p>
                  <p className="text-xs text-gray-500">Ships nationwide</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <FaStore className="text-blue-500 text-lg" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Free Pickup</p>
                  <p className="text-xs text-gray-500">Paradise, TX</p>
                </div>
              </div>
            </div>

            {/* Specifications */}
            {Object.keys(specs).length > 0 && (
              <div className="border-t pt-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Specifications</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(specs).map(([key, val]) => (
                    <div key={key} className="flex justify-between bg-gray-50 rounded-lg px-4 py-2.5">
                      <span className="text-sm text-gray-600">{key}</span>
                      <span className="text-sm font-medium text-gray-900">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Compatible Makes */}
            {product.compatible_makes?.length > 0 && (
              <div className="border-t pt-6">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Compatible Makes</h2>
                <div className="flex flex-wrap gap-2">
                  {product.compatible_makes.map(make => (
                    <span key={make} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                      {make}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">You Might Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map(rp => (
                <Link
                  key={rp.id}
                  to={`/parts/${rp.slug}`}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden group hover:shadow-lg transition-all"
                >
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    {rp.images?.[0] ? (
                      <img src={rp.images[0]} alt={rp.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-5xl text-gray-300">🔧</div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">{rp.name}</h3>
                    <p className="text-base font-bold mt-1">{formatPrice(rp.sale_price || rp.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
